// The inbox under the bell: mentions, replies, review requests and decisions, deliveries, assignments.
// ↑↓ move, Enter jumps to the thing (and marks the row read), Esc closes. Unread rows carry an accent dot.
import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useCollab } from "../../collab/CollabProvider.tsx";
import {
  KIND_LABEL,
  markInboxRead,
  type Notification,
  notificationActor,
  notificationExcerpt,
  notificationRoute,
  notificationThread,
  notificationTitle,
} from "../../collab/inbox.ts";
import { errText, timeAgo, useApp } from "../app.ts";
import { Avatar } from "./Avatars.tsx";

export function InboxPanel({ onClose }: { onClose: () => void }) {
  const c = useCollab()!;
  const { go, api, ws } = useApp();
  const [items, setItems] = useState<Notification[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  const marking = useRef(false);
  const loadSeq = useRef(0);
  const [markingRead, setMarkingRead] = useState(false);

  const load = useCallback(() => {
    if (marking.current) return;
    const seq = ++loadSeq.current;
    c.rapi.inbox({ limit: 40 }).then(
      (r) => {
        if (seq !== loadSeq.current) return;
        setItems(r.items);
        setError(null);
      },
      (e) => seq === loadSeq.current && setError(errText(e)),
    );
  }, [c.rapi]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the unread count moves
  useEffect(() => load(), [load, c.unread]);

  // Focus the first row once the list is there, so arrows work at once.
  useEffect(() => {
    if (focused.current || !items) return;
    focused.current = true;
    (list.current?.querySelector<HTMLElement>("[data-row]") ?? list.current)?.focus();
  }, [items]);

  const markRead = async (ids: string[] | "all") => {
    if (marking.current) return;
    marking.current = true;
    setMarkingRead(true);
    loadSeq.current++;
    setError(null);
    try {
      await markInboxRead(
        items,
        ids,
        () => c.rapi.markRead(ids === "all" ? { all: true } : { ids }),
        setItems,
      );
    } catch (e) {
      setError(`Could not mark notifications read. ${errText(e)}`);
    } finally {
      marking.current = false;
      setMarkingRead(false);
      c.refreshUnread();
    }
  };

  const open = async (n: Notification) => {
    if (!n.read_at) void markRead([n.id]);
    const threadId = notificationThread(n);
    let shotId: string | null = null;
    let episodeOfScene: string | null = null;
    let focus: { threadId: string; assetVersionId?: string | null; takeId?: string | null } | null = threadId
      ? { threadId }
      : null;
    try {
      if (threadId && (n.target_type === "take" || n.target_type === "asset_version")) {
        const t = await c.rapi.thread(threadId);
        shotId = t.shot_id;
        focus = {
          threadId,
          assetVersionId: t.asset_version_id,
          takeId: t.target_type === "take" ? t.target_id : null,
        };
      } else if (n.target_type === "scene" && n.target_id) {
        const s = await api.request<{ episode_id?: string }>(
          "GET",
          `/workspaces/${ws}/scenes/${n.target_id}`,
        );
        episodeOfScene = s.episode_id ?? null;
      }
    } catch {
      // go as near as we can
    }
    const to = notificationRoute(n, { shotId, episodeOfScene });
    c.requestFocus(focus);
    onClose();
    if (to) go(to);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const rows = [...(list.current?.querySelectorAll<HTMLElement>("[data-row]") ?? [])];
    const i = rows.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown") rows[Math.min(rows.length - 1, i + 1)]?.focus();
    else if (e.key === "ArrowUp") rows[Math.max(0, i - 1)]?.focus();
    else if (e.key === "Home") rows[0]?.focus();
    else if (e.key === "End") rows[rows.length - 1]?.focus();
    else if (e.key === "Escape") {
      onClose();
      document.querySelector<HTMLElement>(".pc-bell")?.focus();
    } else return;
    e.preventDefault();
    e.stopPropagation();
  };

  const unread = items?.filter((n) => !n.read_at).length ?? 0;
  return (
    <div className="menu end pc-inbox" role="dialog" aria-label="Inbox" onKeyDown={onKey}>
      <div className="pc-inbox-head">
        <h2>Inbox</h2>
        <span className="grow" />
        {unread > 0 && (
          <button
            type="button"
            className="btn ghost small"
            disabled={markingRead}
            onClick={() => void markRead("all")}
          >
            Mark all read
          </button>
        )}
      </div>
      {error && items && (
        <p className="pc-empty" role="alert">
          {error}
        </p>
      )}
      <div className="pc-inbox-list" ref={list} tabIndex={-1}>
        {error && !items ? (
          <div className="pc-empty">
            <p>The inbox could not be loaded.</p>
            <button type="button" className="btn small" onClick={load}>
              Try again
            </button>
          </div>
        ) : !items ? (
          <div className="pc-empty" role="status" aria-busy="true">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="pc-empty">
            <p className="pc-empty-title">Nothing here yet</p>
            <p>Mentions, replies, sign-off requests and deliveries land here.</p>
          </div>
        ) : (
          items.map((n) => {
            const ex = notificationExcerpt(n);
            return (
              <button
                key={n.id}
                type="button"
                data-row
                className={`pc-note${n.read_at ? "" : " unread"}`}
                onClick={() => void open(n)}
              >
                <Avatar
                  userId={n.actor_id}
                  name={n.actor_name ?? (n.data?.guest_name ? notificationActor(n) : "?")}
                  size={24}
                />
                <span className="pc-note-text">
                  <span className="pc-note-title">
                    {notificationTitle(n)}
                    <span className="pc-note-kind">{KIND_LABEL[n.kind]}</span>
                  </span>
                  {ex && <span className="pc-note-ex">{ex}</span>}
                  <span className="pc-note-time">{timeAgo(n.created_at)}</span>
                </span>
                {!n.read_at && (
                  <>
                    <span className="pc-unread-dot" aria-hidden="true" />
                    <span className="sr-only">unread</span>
                  </>
                )}
              </button>
            );
          })
        )}
      </div>
      <div className="pc-inbox-foot" aria-hidden="true">
        <kbd>↑</kbd>
        <kbd>↓</kbd> move <kbd>↵</kbd> open <kbd>esc</kbd> close
      </div>
    </div>
  );
}
